import React, { useState } from 'react';

const PLATFORMS = [
  { key: 'web', label: 'Web (SEO)' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'youtube', label: 'YouTube' }
];

const STRATEGIES = ['direct', 'seo', 'curiosity', 'emotional', 'listicle'];

const STRATEGY_COLORS = {
  direct: { bg: '#e8f0fe', text: '#1a73e8', label: 'Direct' },
  seo: { bg: '#e6f4ea', text: '#137333', label: 'SEO' },
  curiosity: { bg: '#fef7e0', text: '#b06000', label: 'Curiosity' },
  emotional: { bg: '#fce8e6', text: '#c5221f', label: 'Emotional' },
  listicle: { bg: '#f3e8fd', text: '#7627bb', label: 'Listicle' }
};

// Custom in-DOM dropdown. Replaces the native <select> whose popup is an OS-level overlay
// that renders in inconsistent/wrong positions. This one is fully app-controlled.
const PlatformDropdown = ({ platformKey, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = PLATFORMS.find(p => p.key === platformKey);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1',
          fontSize: '0.95rem', fontWeight: '600', color: '#1a1a2e',
          backgroundColor: '#f8f9fa', cursor: 'pointer'
        }}
      >
        {current?.label}
        <i className="bi bi-chevron-down" style={{ fontSize: '0.7rem', color: '#64748b' }}></i>
      </button>
      {open && (
        <>
          {/* Full-screen backdrop closes the menu on any outside click */}
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div role="listbox" style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 41, minWidth: '170px',
            backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '4px'
          }}>
            {PLATFORMS.map(p => {
              const active = p.key === platformKey;
              return (
                <div
                  key={p.key}
                  role="option"
                  aria-selected={active}
                  onClick={() => { onChange(p.key); setOpen(false); }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  style={{
                    padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.9rem',
                    fontWeight: active ? '600' : '500', color: active ? '#1a73e8' : '#1a1a2e',
                    backgroundColor: active ? '#e8f0fe' : 'transparent', cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                >
                  {p.label}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const PlatformColumn = ({ platformKey, byPlatform, copiedId, onCopy, getCounterColor, onChangePlatform }) => {
  const charLimit = byPlatform[platformKey]?.[STRATEGIES[0]]?.char_limit;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
        <PlatformDropdown platformKey={platformKey} onChange={onChangePlatform} />
        {charLimit && (
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            max {charLimit} chars
          </div>
        )}
      </div>
      {STRATEGIES.map(strategy => {
        const headline = byPlatform[platformKey]?.[strategy];
        const colors = STRATEGY_COLORS[strategy];
        const cellId = `${platformKey}-${strategy}`;

        if (!headline) {
          return (
            <div key={cellId} style={{
              padding: '0.75rem',
              marginBottom: '0.5rem',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              color: '#94a3b8',
              fontSize: '0.85rem'
            }}>
              —
            </div>
          );
        }

        return (
          <div key={cellId} style={{
            padding: '0.75rem',
            marginBottom: '0.5rem',
            borderRadius: '8px',
            border: '1px solid #f1f5f9',
            backgroundColor: '#fff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '0.2rem 0.5rem',
                  backgroundColor: colors.bg,
                  color: colors.text,
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  {colors.label}
                </span>
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: getCounterColor(headline.char_count, headline.char_limit)
                }}>
                  {headline.char_count}/{headline.char_limit}
                </span>
              </div>
              <button
                onClick={() => onCopy(headline.headline_text, cellId)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  color: copiedId === cellId ? '#198754' : '#94a3b8',
                  fontSize: '0.85rem'
                }}
                title={copiedId === cellId ? 'Copied!' : 'Copy headline'}
              >
                <i className={`bi ${copiedId === cellId ? 'bi-check' : 'bi-files'}`}></i>
              </button>
            </div>
            <div style={{ fontSize: '0.85rem', lineHeight: '1.5', color: '#1a1a2e' }}>
              {headline.headline_text}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const HeadlinesDisplay = ({ headlines }) => {
  const [copiedId, setCopiedId] = useState(null);
  const [leftPlatform, setLeftPlatform] = useState('web');
  const [rightPlatform, setRightPlatform] = useState('facebook');
  const [mobilePlatform, setMobilePlatform] = useState('web');

  if (!headlines || headlines.length === 0) return null;

  // Group headlines by platform
  const byPlatform = {};
  for (const h of headlines) {
    if (!byPlatform[h.platform]) byPlatform[h.platform] = {};
    byPlatform[h.platform][h.strategy] = h;
  }

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getCounterColor = (count, limit) => {
    const ratio = count / limit;
    if (ratio > 1) return '#c5221f';
    if (ratio >= 0.8) return '#b06000';
    return '#137333';
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      marginBottom: '2rem',
      overflow: 'hidden'
    }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9' }}>
        <h5 style={{ fontWeight: '600', color: '#1a1a2e', margin: 0 }}>Alternative Headlines</h5>
      </div>

      {/* Desktop: 2 columns */}
      <div className="d-none d-md-grid" style={{ padding: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <PlatformColumn
          platformKey={leftPlatform}
          byPlatform={byPlatform}
          copiedId={copiedId}
          onCopy={handleCopy}
          getCounterColor={getCounterColor}
          onChangePlatform={setLeftPlatform}
        />
        <PlatformColumn
          platformKey={rightPlatform}
          byPlatform={byPlatform}
          copiedId={copiedId}
          onCopy={handleCopy}
          getCounterColor={getCounterColor}
          onChangePlatform={setRightPlatform}
        />
      </div>

      {/* Mobile: 1 column */}
      <div className="d-md-none" style={{ padding: '1rem' }}>
        <PlatformColumn
          platformKey={mobilePlatform}
          byPlatform={byPlatform}
          copiedId={copiedId}
          onCopy={handleCopy}
          getCounterColor={getCounterColor}
          onChangePlatform={setMobilePlatform}
        />
      </div>
    </div>
  );
};

export default HeadlinesDisplay;
